import { useState, useEffect, useRef, useMemo } from "react";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import emailjs from "emailjs-com"; // <–– emailjs käytössä
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import contactVideo from "../../assets/contactVideo.mp4";
import RentalDateSelector from "../../components/RentalDateSelector/RentalDateSelector";
import { useBooking } from "../../context/BookingContext";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { Link } from "react-router-dom";
// import Confetti from "react-confetti";
import RecaptchaField from "../../components/RecaptchaField/RecaptchaField";
import ContactPageHelmetComponent from "./ContactPageHelmetComponent";
// import CheckoutForm from "../../components/CheckoutForm/CheckoutForm";
// import { useCallback } from "react";
const libraries = ["places"];

const pageShellClass =
  "relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-page via-surface2 to-[#E9FBF7] px-4 py-24 text-[#111827] dark:from-darkpage dark:via-darksurface dark:to-darksurface2 dark:text-white sm:px-6 lg:px-8";

const formCardClass =
  "relative mx-auto w-full max-w-6xl overflow-hidden rounded-[2rem] border border-[#DDE7E5] bg-white/90 p-6 shadow-xl backdrop-blur-xl dark:border-darkborder dark:bg-[#111114]/95 sm:p-8 lg:p-10";

const badgeClass =
  "mb-5 inline-flex rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 font-customFont1 text-xs font-extrabold uppercase tracking-[0.14em] !text-[#111827] shadow-sm dark:!text-white";

const titleClass =
  "font-customFont2 font-bold uppercase leading-tight text-[#111827] dark:text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl";

const introTextClass =
  "mt-5 font-customFont1 text-lg leading-relaxed !text-[#374151] dark:!text-[#D1D5DB] sm:text-xl [&_strong]:font-extrabold [&_strong]:!text-[#111827] dark:[&_strong]:!text-white";

const sectionBoxClass =
  "rounded-3xl border border-[#DDE7E5] bg-[#F8FAFC]/80 p-5 shadow-sm dark:border-darkborder dark:bg-darksurface/80";

const innerSectionClass =
  "mt-6 rounded-3xl border border-[#DDE7E5] bg-[#F8FAFC]/80 p-5 shadow-sm dark:border-darkborder dark:bg-darksurface/80";

const sectionTitleClass =
  "font-customFont2 text-3xl font-bold uppercase leading-none !text-[#111827] dark:!text-white";

const labelClass =
  "mb-2 block font-customFont1 text-sm font-extrabold uppercase tracking-[0.1em] !text-[#111827] dark:!text-white";

const inputClass =
  "w-full rounded-2xl border border-[#DDE7E5] bg-white px-4 py-3 font-customFont1 text-base font-semibold text-[#111827] shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 dark:border-darkborder dark:bg-darksurface dark:text-white";

const disabledInputClass =
  "w-full rounded-2xl border border-[#DDE7E5] bg-gray-200 px-4 py-3 font-customFont1 text-base font-semibold text-gray-500 opacity-50 shadow-sm outline-none disabled:cursor-not-allowed dark:border-darkborder dark:bg-darksurface dark:text-gray-500";

const errorTextClass =
  "mt-1 font-customFont1 text-sm font-bold text-red-600 dark:text-red-300";

const radioTextClass =
  "font-customFont1 text-base font-bold !text-[#111827] dark:!text-white";

/**
 * Yhdistetty laskentafunktio
 * Laskee vuokrauksen kokonaispäivät (pyöristää ylöspäin, jos vuokraus ylittää täyden vuorokauden edes tunnilla)
 */
const calculateRentalPrice = (
  start,
  end,
  pricePerDay,
  quantity,
) => {
  if (!start || !end || !pricePerDay) return 0;

  const startDate = new Date(start);
  const endDate = new Date(end);

  const msInHour = 1000 * 60 * 60;
  const msInDay = msInHour * 24;

  const diffMs = Math.max(endDate - startDate, msInHour); // 🔥 vähintään 1h
  const days = Math.max(1, Math.ceil(diffMs / msInDay)); // 🔥 vähintään 1 vrk

  return days * Number(pricePerDay) * (quantity || 1);
};

// 🧮 Frontend-yhteissumma (ennen backendin vahvistusta)
const calculateCartTotalFrontend = (cart, selectedDates) => {
  return cart.reduce((sum, item) => {
    // 🚚 Peräkärry: käytä totalPrice jos olemassa
    if (item.flags?.isTrailer) {
      return sum + Number(item.pricing?.totalPrice ?? 0);
    }

    // 🔊 Laitteet: päivähinta × määrä × vuorokaudet
    const unitPrice = Number(
      item.pricing?.unitPrice ??
      item.price ??
      0
    );

    const quantity = Number(item.quantity ?? 1);

    const days = selectedDates?.startDate && selectedDates?.endDate
      ? Math.max(
          1,
          Math.ceil(
            (new Date(selectedDates.endDate) - new Date(selectedDates.startDate)) /
              (1000 * 60 * 60 * 24)
          )
        )
      : 1;

    return sum + unitPrice * quantity * days;
  }, 0);
};

 // 💶 Rahapyöristys 2 desimaaliin (välttää JS float -yllätyksiä)
 const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const calculateTransportCostFrontend = ({
  deliveryRequired,
  deliveryDistance,
  transportNeedsTrailer,
}) => {
  if (!deliveryRequired || deliveryDistance == null) return 0;

 const PRICE_PER_KM = 0.59; // ✅ todellinen km-korvaus
 const TRAILER_FEE_PER_DIRECTION = 20;

 const totalKm = deliveryDistance * 2; // edestakainen
 const kmCost = round2(totalKm * PRICE_PER_KM);
 const trailerFee = transportNeedsTrailer
   ? TRAILER_FEE_PER_DIRECTION * 2
   : 0;

 return round2(kmCost + trailerFee);
};



const normalizeCart = (rawCart = []) =>
  rawCart.map((item) => ({
    ...item,

    // 🔥 Hinta-normalisointi (nykyinen)
    price: Number(
      item.pricing?.unitPrice ??
      item.price ??
      item.price_per_day ??
      0
    ),
    quantity: Number(item.quantity || 1),

    // ✅ UUSI: deliveryClass-normalisointi (yhden totuuden lähde)
    deliveryClass:
      item.attributes?.deliveryClass ??
      item.attributes?.delivery_class ??
      item.deliveryClass ??
      item.delivery_class ??
      item.meta?.deliveryClass ??
      null,
  }));

const Contactpage = () => {
  // AOS
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  // *******************************
  //  1) ENV-muuttujat + ASETUKSET
  // *******************************
  const EMAILJS_USER_ID = process.env.REACT_APP_EMAILJS_USER_ID;
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const EMAILJS_TEMPLATE_ID_OWNER = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_OWNER;
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const HOME_ADDRESS = process.env.REACT_APP_HOME_ADDRESS || "Näsiätie 2, Kerava";
  const RENTAL_API_BASE = process.env.REACT_APP_RENTAL_API_BASE;

 if (!RENTAL_API_BASE) {
   console.error("REACT_APP_RENTAL_API_BASE puuttuu ympäristömuuttujista");
 }

  // *******************************
  //  2) OSTOSKORI & PERÄKÄRRY-LOGIIKKA
  // *******************************
  // Yhdistetään tarvittavat tilat BookingContextista
  const { trailerPickupTime, setTrailerPickupTime, selectedDates, setSelectedDates } = useBooking();

  const [cart, setCart] = useState([]);
  // const [showConfetti, setShowConfetti] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState("pickup");
  const [deliveryDistance, setDeliveryDistance] = useState(null);

  // Radiovalinta: varaus vs. pelkkä viesti
  const [autoMessageOption, setAutoMessageOption] = useState("reservation");
  // Lisäkenttä: vetoauton rekisterinumero
  const [trailerLicensePlate, setTrailerLicensePlate] = useState("");
  // Määritellään hasTrailer, joka etsii trailer-tuotteen korista

 // 🛒 Onko peräkärry tuotteena korissa
 const hasTrailerInCart = useMemo(() => {
   return cart.some(
     (item) =>
       item.flags?.isTrailer === true ||
       item.attributes?.deliveryClass === "trailer"
   );
 }, [cart]);

 // 🚚 Kuljetuslogiikka deliveryClass-attribuuteilla
// 🚚 Kuljetuslogiikka – PA + monitorit = kärry
const transportNeedsTrailer = useMemo(() => {
  let paCount = 0;
  let monitorCount = 0;

  for (const item of cart) {
    const name = item.name?.toLowerCase() || "";

    // PA-paketit
    if (name.includes("pa-äänentoisto")) {
      paCount += item.quantity || 1;
    }

    // Monitorit (lavakaiuttimet)
    if (
      name.includes("monitori") ||
      name.includes("monitor")
    ) {
      monitorCount += item.quantity || 1;
    }
  }

  // SÄÄNNÖT:
  // 1) 2× PA → aina kärry
  // 2) PA + monitorit → aina kärry
  return paCount >= 2 || (paCount >= 1 && monitorCount >= 1);
}, [cart]);


 // 🔔 UI-flag: peräkärryn vuokraus-UI vain jos kärry on korissa
 const showTrailerNotice = hasTrailerInCart;

  useEffect(() => {
    setCart(
      normalizeCart(
        JSON.parse(localStorage.getItem("rentalCart")) || []
      )
    );
  }, []);

  useEffect(() => {
    const handleCartUpdate = () => {
      setCart(
        normalizeCart(
          JSON.parse(localStorage.getItem("rentalCart")) || []
        )
      );
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, []);

  
  // Aseta trailerPickupTime käyttäen selectedDates.startDate (muuntaa ajan "23.00" -> "23:00")
  useEffect(() => {
    if (selectedDates?.startDate) {
      const date = new Date(selectedDates.startDate);
      const raw = date.toLocaleTimeString("fi-FI", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/Helsinki",
      });
      const formatted = raw.replace(".", ":");
      setTrailerPickupTime(formatted);
    }
  }, [selectedDates?.startDate, setTrailerPickupTime]);


  // *******************************
  //  3) LOMAKKEEN TILAT
  // *******************************
  const [nimi, setNimi] = useState("");
  const [sahkoposti, setSahkoposti] = useState("");
  const [puhelinnumero, setPuhelinnumero] = useState("");
  const [emailError, setEmailError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("invoice");
  const [reservationResult, setReservationResult] = useState(null);
  const validateEmail = (value) => {
    const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailPattern.test(value)) {
      setEmailError("Anna kelvollinen sähköpostiosoite");
    } else {
      setEmailError("");
    }
  };
  

const [phoneError, setPhoneError] = useState("");

const validatePhone = (value) => {
  const phonePattern = /^[0-9\s\-+()]{7,20}$/;
  if (!phonePattern.test(value)) {
    setPhoneError("Anna kelvollinen puhelinnumero");
  } else {
    setPhoneError("");
  }
};

  const [tapahtumapaikka, setTapahtumapaikka] = useState("");
  const [viesti, setViesti] = useState("");
  const [recaptchaRentalValue, setRecaptchaRentalValue] = useState(null);
  const [formError, setFormError] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // const [maksuSuoritettu, setMaksuSuoritettu] = useState(false);

  const cleanAddress = deliveryAddress.trim();
  const deliveryRequired = deliveryOption === "delivery";

  // 🚫 Jos kärry toimitetaan, asiakkaalla ei ole vetoautoa → tyhjennä rekisterinumero
 useEffect(() => {
   if (deliveryOption === "delivery") {
     setTrailerLicensePlate("");
   }
 }, [deliveryOption]);

  const isValidAddress =
    cleanAddress && cleanAddress.length > 5 && cleanAddress.includes(" ");
  
// *******************************
//  4) GOOGLE AUTOCOMPLETE
// *******************************
const recaptchaRef = useRef(null);

// 🔑 YKSI REF PER AUTOCOMPLETE
const trailerDeliveryRef = useRef(null); // peräkärryn toimitus
const deviceDeliveryRef = useRef(null);  // laitteiden toimitus
const locationRef = useRef(null); // tapahtumapaikka (ei kuljetusosoite)

const autocompleteOptions = {
  componentRestrictions: { country: "fi" },
};

  // *******************************
  //  5) MUUT EFFECTIT & APUFUNKTIOT
  // *******************************
useEffect(() => {
  if (cart.length === 0) {
    setDeliveryAddress("");
    setDeliveryDistance(null);
  }
}, [cart]);



useEffect(() => {
  console.log("GOOGLE LOADED:", !!window.google?.maps, "PLACES:", !!window.google?.maps?.places);
}, []);


  
// Google Distance
const calculateDistance = (destinationLocation) => {
  if (!window.google?.maps) {
    console.warn("Google Maps API ei ole vielä ladattu");
    return;
  }

  const service = new window.google.maps.DistanceMatrixService();

    service.getDistanceMatrix(
      {
        origins: [HOME_ADDRESS],
        destinations: [destinationLocation],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status === "OK") {
          const element = response.rows[0].elements[0];
          if (element.status === "OK") {
            const distanceInKm = element.distance.value / 1000;
            setDeliveryDistance(distanceInKm);
          } else {
            setFormError(`Matkan laskeminen epäonnistui: ${element.status}`);
            setDeliveryDistance(null);
          }
        } else {
          setFormError(`Matkan laskeminen epäonnistui. Google API status: ${status}`);
          setDeliveryDistance(null);
        }
      }
    );
  };


// *******************************
//  6) LOMAKKEEN LÄHETYS
// *******************************

const handleFormSubmit = async (e) => {
  e.preventDefault();
  setFormError(null);

  if (!recaptchaRentalValue) {
    toast.warning("Vahvista että et ole botti reCAPTCHA-testillä.");
    return;
  }

// (A) PELKKÄ VIESTI
if (autoMessageOption === "message") {
  const missing = [];
  if (!nimi) missing.push("Nimi");
  if (!sahkoposti) missing.push("Sähköposti");
  if (!puhelinnumero) missing.push("Puhelinnumero");
  if (!viesti) missing.push("Viestisi");

  if (missing.length > 0) {
    toast.warning(`Puuttuvat kentät: ${missing.join(", ")}`);
    return;
  }

 // =========================
 // SÄHKÖPOSTIN TUOTEYHTEENVETO
 // =========================
 const cartSummary = cart
   .map((item) => {
     const qty = item.quantity || 1;
     return `${qty} x ${item.name}`;
   })
   .join("<br/>");

  const templateParamsOwner = {
    user_name: nimi,
    user_email: sahkoposti,
    user_phone: puhelinnumero,
    place: tapahtumapaikka || deliveryAddress || "", // tämä rivi lisää tapahtumapaikan
    greeting_text: `Hei!`,
    info_text: `Olet saanut uuden viestin lomakkeen kautta.`,
    user_message_section: viesti.replace(/\n/g, "<br/>"),
    
    // Tyhjennetään kaikki muut kentät
    cart_summary: cartSummary,
    total_price: "",
    remaining_amount: "",
    reservation_fee_line: "",
    trailer_info_section: "",
    payment_info_section: "",
    info_section: "",
    delivery_info_section: "",
    closing_text: "",
    logo_url: "https://spiderlegsproductions.com/assets/new_logo.webp"
  };

  try {
    const ownerResult = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID_OWNER,
      templateParamsOwner,
      EMAILJS_USER_ID
    );

    if (ownerResult.status === 200) {
      toast.success("Viestisi on lähetetty! Otamme sinuun pian yhteyttä.");
      setNimi("");
      setSahkoposti("");
      setPuhelinnumero("");
      setViesti("");
    } else {
      toast.error("Viestin lähetys epäonnistui.");
    }
  } catch (error) {
    console.error("Virhe viestin lähetyksessä:", error);
    toast.error("Jotain meni pieleen.");
  }

  return;
}


  // (B) VARAUS (laitteet/peräkärry)
  let missingFields = [];
  if (!nimi) missingFields.push("Nimi");
  if (!sahkoposti) missingFields.push("Sähköposti");
  if (!puhelinnumero) missingFields.push("Puhelinnumero");
  if (!selectedDates?.startDate) missingFields.push("Aloituspäivä");
  if (!selectedDates?.endDate) missingFields.push("Loppupäivä");
  if (!cart?.length) missingFields.push("Ostoskorisi on tyhjä");
  if (transportNeedsTrailer && deliveryOption === "pickup" && !trailerLicensePlate.trim()) {
   missingFields.push("Rekisterinumero");
 }
  // Kuljetus?
  if (deliveryRequired && !isValidAddress) missingFields.push("Toimitusosoite");
 if (deliveryRequired && deliveryDistance == null) {
   toast.error("Kuljetusvalinta tehty, mutta matkaa ei ole laskettu.");
   return;
 }

  if (missingFields.length > 0) {
    toast.warning(`Puuttuvat kentät: ${missingFields.join(", ")}`);
    return;
  }

  // Tarkista päivämäärien kelvollisuus
 const isValid = await validateReservation();
 if (!isValid) return;

  // // Tallenna backendiin tms.
  // const formData = {
  //   nimi,
  //   sahkoposti,
  //   puhelinnumero,
  //   tapahtumapaikka,
  //   deliveryAddress,
  //   trailerLicensePlate,
  //   start_date: selectedDates.startDate,
  //   end_date: selectedDates.endDate,
  //   cart: cart.map((item) => ({ id: item.id, quantity: item.quantity })),
  //   viesti,
  //   userId: userId ? parseInt(userId) : null, // 🔥 TÄSSÄ LISÄTÄÄN userId
  // };

  try {

    const response = await fetch(
  `${RENTAL_API_BASE}/api/reservations`,
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      nimi,
      sahkoposti,
      puhelinnumero,
      start_date: selectedDates.startDate,
      end_date: selectedDates.endDate,
      message: viesti || null,
      cart: cart.map((item) => ({
        id: item.id,
        quantity: item.quantity,
      })),
      delivery_required: deliveryRequired,
      delivery_address: deliveryRequired ? deliveryAddress : null,
     distance_km: deliveryRequired ? Number(deliveryDistance) : null,
      trailer_license_plate: transportNeedsTrailer ? trailerLicensePlate : null,
      payment_method: paymentMethod,
      
    }),
  }
);

const result = await response.json();

if (!response.ok || !result.ok) {
  throw new Error("Varaus epäonnistui backendissä");
}

setReservationResult(result);

// const reservationId = result.reservation_id;

    // Lähetä
    // const [customerRes, ownerRes] = await Promise.all([
    //   emailjs.send(
    //     EMAILJS_SERVICE_ID,
    //     EMAILJS_TEMPLATE_ID_CUSTOMER,
    //     templateParamsCustomer,
    //     EMAILJS_USER_ID
    //   ),
    //   emailjs.send(
    //     EMAILJS_SERVICE_ID,
    //     EMAILJS_TEMPLATE_ID_OWNER,
    //     templateParamsOwner,
    //     EMAILJS_USER_ID
    //   ),
    // ]);

    toast.success("Varaus on luotu, mutta se astuu voimaan vasta kun vahvistat sen sähköpostista.", {
      autoClose: 6000,
    });

    localStorage.removeItem("selectedBookingDates");
    localStorage.removeItem("modalBookingData");
    localStorage.removeItem("rentalCart");
    localStorage.removeItem("trailerPickupTime");

    setSelectedDates({
      startDate: null,
      endDate: null,
    });

    setNimi("");
    setSahkoposti("");
    setPuhelinnumero("");
    setTapahtumapaikka("");
    setViesti("");
    setCart([]);
    window.dispatchEvent(new Event("cartUpdated"));

  } catch (error) {
    console.error("Virhe:", error);
    toast.error("Tietojen lähettäminen epäonnistui.");
  }
};

  const validateReservation = async () => {
    let errors = [];
  
    // 1. Ostoskori tyhjä
    if (!Array.isArray(cart) || cart.length === 0) {
      errors.push("Valitse ensin vuokrattavat tuotteet.");
    }
  
    // 2. Päivämäärät puuttuvat
    if (!selectedDates.startDate || !selectedDates.endDate) {
      errors.push("Syötä molemmat päivämäärät.");
    }
  
    // 3. Aloituspäivä myöhäisempi kuin loppupäivä
    if (
      selectedDates.startDate &&
      selectedDates.endDate &&
      new Date(selectedDates.startDate) > new Date(selectedDates.endDate)
    ) {
      errors.push("Aloituspäivä ei voi olla loppupäivää myöhemmin.");
    }
  
    // 4. Valinnainen: estetään menneisyyteen varaaminen
    const now = new Date();
    if (
      selectedDates.startDate &&
      new Date(selectedDates.startDate).setHours(0, 0, 0, 0) < now.setHours(0, 0, 0, 0)
    ) {
      errors.push("Aloituspäivä ei voi olla menneisyydessä.");
    }
  
    // Jos virheitä löytyi, näytetään ne
    if (errors.length > 0) {
      setFormError(errors.join(" "));
      return false;
    }
  
    return true;
  };
  
  // Poistetaan tuote korista
const handleRemoveFromCart = (index) => {
  try {
    const updatedCart = [...cart];
    const removedItem = updatedCart.splice(index, 1)[0];
    setCart(updatedCart);
    localStorage.setItem("rentalCart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("cartUpdated"));
    toast.info(`${removedItem.name} on poistettu vuokrauskorista.`);
  } catch (error) {
    console.error("Tuotteen poistaminen epäonnistui:", error);
    toast.error("Tuotteen poistaminen epäonnistui. Yritä myöhemmin.");
  }
};

// 🔍 Frontend-erittelyä varten
const frontendTransportCost =
  deliveryRequired && deliveryDistance != null
    ? calculateTransportCostFrontend({
        deliveryRequired,
        deliveryDistance,
        transportNeedsTrailer,
      })
    : 0;

const frontendRentalTotal = calculateCartTotalFrontend(cart, selectedDates);


  // *******************************
  //  7) RENDERÖINTI
  // *******************************

  return (
    <>
    <ContactPageHelmetComponent />

   <LoadScript
      googleMapsApiKey={GOOGLE_MAPS_API_KEY}
      libraries={libraries}
    >

      {/* {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={800}
          recycle={false}
        />
      )} */}

      <main className={pageShellClass}>
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 h-full min-h-screen w-full object-cover opacity-10 dark:opacity-16"
        >
          <source src={contactVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute left-1/2 top-16 h-72 w-72
            -translate-x-1/2 rounded-full bg-primary/15 blur-3xl
            dark:bg-primary/10
          "
        />

        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute -right-24 top-96 h-96 w-96
            rounded-full bg-[#30f2cf]/10 blur-3xl
            dark:bg-primary/10
          "
        />

        <div className="relative flex min-h-screen flex-col items-center justify-center">
          <motion.div
            className={formCardClass}
            data-aos="fade-in"
            layout
          >
            <div
              aria-hidden="true"
              className="
                pointer-events-none absolute -right-20 -top-20 h-56 w-56
                rounded-full bg-primary/10 blur-3xl
                dark:bg-primary/10
              "
            />

            <div className="relative">
              <span className={badgeClass}>Varaus ja yhteydenotto</span>

              <h1
                className={titleClass}
                data-aos="fade-in"
                data-aos-duration="1500"
              >
                Tapahtumatekniikan varaus ja yhteydenotto
              </h1>
            </div>

              {/* Radiovalinta: varaus / viesti */}
              <div className={`${sectionBoxClass} my-6`}>
                <p className="font-customFont1 text-sm font-extrabold uppercase tracking-[0.12em] !text-[#111827] dark:!text-white">
                      Valitse, mitä haluat tehdä
                    </p>
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="autoMessageOption"
                          value="reservation"
                          checked={autoMessageOption === "reservation"}
                          onChange={() => setAutoMessageOption("reservation")}
                          className="form-radio text-primary"
                        />
                        <span className="ml-2 font-customFont1 text-base font-bold !text-[#111827] dark:!text-white">
                          Tehdä vuokrakaluston varauksen.
                        </span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="autoMessageOption"
                          value="message"
                          checked={autoMessageOption === "message"}
                          onChange={() => setAutoMessageOption("message")}
                          className="form-radio text-primary"
                        />
                        <span className="ml-2 font-customFont1 text-base font-bold !text-[#111827] dark:!text-white">
                          Lähettää viestin.
                        </span>
                      </label>
                    </div>
                  </div>

            {formError && (
              <p className="text-red-500 text-sm mt-2">{formError}</p>
            )}

            <p className={`${introTextClass} pb-4`}>
              {autoMessageOption === "message" ? (
                <>
                Olipa kyse palautteesta, palveluihin liittyvistä kysymyksistä tai yhteistyöehdotuksesta, älä epäröi ottaa yhteyttä.
                  <br />
                </>
              ) : showTrailerNotice ? (
                <>
                  Täytä alla oleva lomake vuokrataksesi <strong>HIRVI 350 -kuomukärryn</strong>.
                  <br />
                  Saat sähköpostiisi yhteenvedon ja ohjeet varauksen vahvistamiseen sekä noutoon.
                  <br />
                  Halutessasi voit myös <strong>valita kärryn toimituksen</strong> suoraan haluamaasi osoitteeseen.
                </>
              ) : (
                <>
                  Täytä alla oleva lomake varataksesi vuokrauskorissa olevan kaluston.
                  <br />
                  Sen jälkeen saat yhteenvedon sähköpostiisi ja ohjeet varauksen vahvistamiseen.
                </>
              )}
            </p>

            <div className="my-4">
              <Link
                to="/palvelut#perakarryn-vuokraus"
                className="text-linkcol underline hover:text-accent transition font-customFont1 text-base mb-16 scroll-mt-28"
              >
                📄 Lue lisää kuljetuspalvelusta, vuokrakärrystä ja noutamisesta
              </Link>
            </div>

            <div
              className="grid grid-cols-1 xxxsm:grid-cols-1 xxsm:grid-cols-1 s24:grid-cols-1 xsm:grid-cols-1 sm:grid-cols-1 md:grid-cols-2 mdd:grid-cols-2  lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-4"
              data-aos="fade-right"
              data-aos-duration="1000"
            >
              {/* Lomake vasemmalla */}
              <div>
                <form onSubmit={handleFormSubmit}>

                

                  {/* Päivämäärät vain varaus-tilassa */}
                  {autoMessageOption === "reservation" && (
                    <motion.div
                      className="min-h-[80px]"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <RentalDateSelector labelColor="#30F2CF" />

                     
                    </motion.div>
                  )}

                  {/* Peräkärry-lisäkentät (vain reservation + hasTrailer) */}
                  {showTrailerNotice && (
                    <div className={innerSectionClass}>
                      <h3 className={sectionTitleClass}>
                        Peräkärryn vuokraus
                      </h3>

                      <div className="mb-4">
                        <label htmlFor="trailerTime" className={labelClass}>
                          Vuokraus alkaa
                        </label>
                        <input
                          id="trailerTime"
                          type="time"
                          value={trailerPickupTime}
                          onChange={(e) => {
                            setTrailerPickupTime(e.target.value);
                          }}
                          className={`${inputClass} text-center`}
                        />

                        {selectedDates.endDate && (
                          <p className="my-2 font-customFont1 text-sm font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                            Valittu palautusaika:{" "}
                            {(() => {
                              const end = new Date(selectedDates.endDate);
                              return `${end.toLocaleDateString("fi-FI", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })} klo ${end.toLocaleTimeString("fi-FI", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })}`;
                            })()}
                          </p>
                        )}


                      </div>

                      <div className="mb-4">
                        <label htmlFor="trailerLicensePlate" className={labelClass}>
                          Vetoauton rekisterinumero
                        </label>
                      <input
                        id="trailerLicensePlate"
                        type="text"
                        placeholder="ABC-123"
                        pattern="[A-ZÅÄÖ]{2,3}-[0-9]{1,3}"
                        value={trailerLicensePlate}
                        onChange={(e) =>
                          setTrailerLicensePlate(e.target.value.toUpperCase())
                        }
                        disabled={deliveryOption === "delivery"}
                          className={
                            deliveryOption === "delivery"
                              ? `${disabledInputClass} text-center uppercase`
                              : `${inputClass} text-center uppercase`
                          }
                      />
                      </div>

                      {/* haluatko toimituksen */}
                      <fieldset className="mb-4 rounded-3xl border border-primary/30 bg-primary/10 p-4 dark:border-primary/30 dark:bg-primary/5">
                        <legend className={labelClass}>
                          Haluatko, että kärry toimitetaan?
                        </legend>
                        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                          <label htmlFor="deliveryOptionPickup" className="flex items-center space-x-2">
                            <input
                              id="deliveryOptionPickup"
                              type="radio"
                              name="deliveryOption"
                              value="pickup"
                              checked={deliveryOption === "pickup"}
                              onChange={() => setDeliveryOption("pickup")}
                              className="form-radio"
                            />
                            <span className={radioTextClass}>
                              En, noudan itse.
                            </span>
                          </label>

                          <label htmlFor="deliveryOptionDelivery" className="flex items-center space-x-2">
                            <input
                              id="deliveryOptionDelivery"
                              type="radio"
                              name="deliveryOption"
                              value="delivery"
                              checked={deliveryOption === "delivery"}
                              onChange={() => setDeliveryOption("delivery")}
                              className="form-radio"
                            />
                            <span className={radioTextClass}>
                              Haluan toimituksen
                            </span>
                          </label>
                        </div>
                      </fieldset>

                      <div className="mb-4">
                       <label
                          htmlFor="deliveryAddress"
                          className={
                            deliveryOption !== "delivery"
                              ? "mb-2 block font-customFont1 text-sm font-extrabold uppercase tracking-[0.1em] text-gray-500 opacity-50"
                              : labelClass
                          }
                        >
                          Toimitusosoite
                        </label>
                          <Autocomplete
                 

                          onLoad={(ref) => (trailerDeliveryRef.current = ref)}
                            onPlaceChanged={() => {

                            const place = trailerDeliveryRef.current?.getPlace();
                              if (place?.geometry) {
                                setDeliveryAddress(place.formatted_address);
                                calculateDistance(place.geometry.location);
                              }
                            }}
                            options={autocompleteOptions}
                          >
                          <input
                            id="deliveryAddress"
                            type="text"
                            placeholder="Anna toimitusosoite..."
                            value={deliveryAddress}
                            onChange={(e) => {
                              setDeliveryAddress(e.target.value);
                              if (e.target.value.trim().length < 3) {
                                setDeliveryDistance(null);
                              }
                            }}
                            
                            disabled={deliveryOption !== "delivery"}
                            aria-invalid={deliveryRequired && !isValidAddress} // ✅ Oikeassa paikassa, boolean-arvo
                          className={
                            deliveryOption !== "delivery"
                              ? `${disabledInputClass} text-center`
                              : !isValidAddress && deliveryRequired
                                ? `${inputClass} border-red-500 text-center focus:border-red-500 focus:ring-red-500/30`
                                : `${inputClass} text-center`
                          }
                          />
                        </Autocomplete>

                        {/* ✅ Näytetään virheilmoitus vain jos kuljetus on valittu JA osoite virheellinen */}
                        {deliveryRequired && !isValidAddress && (
                          <p className={errorTextClass}>Anna kelvollinen osoite</p>
                        )}

                      </div>
                    </div>
                  )}

                 {autoMessageOption === "reservation" && !hasTrailerInCart && (

                    <div className={innerSectionClass}>
                      <h3 className={sectionTitleClass}>
                        Laitteiden vuokraus
                      </h3>
                      <div className="mb-4">
                        <label
                          htmlFor="deliveryOptionSelect"
                          className={labelClass}
                        >
                          Haluatko, että toimitamme laitteet tapahtumapaikalle?
                        </label>
                        <select
                          id="deliveryOptionSelect"
                          value={deliveryOption}
                          onChange={(e) => {
                            setDeliveryOption(e.target.value);
                            if (e.target.value === "pickup") setDeliveryAddress("");
                          }}
                          className={inputClass}
                        >
                          <option value="pickup">En, noudan itse</option>
                          <option value="delivery">Kyllä, haluan toimituksen</option>
                        </select>
                      </div>

                      <div className="mb-4">
                       <label
                          htmlFor="deliveryAddress"
                          className={
                            deliveryOption !== "delivery"
                              ? "mb-2 block font-customFont1 text-sm font-extrabold uppercase tracking-[0.1em] text-gray-500 opacity-50"
                              : labelClass
                          }
                        >
                          Toimitusosoite
                        </label>
                        <Autocomplete
                          onLoad={(ref) => (deviceDeliveryRef.current = ref)}
                            onPlaceChanged={() => {
                              const place = deviceDeliveryRef.current?.getPlace();
                              if (place?.geometry) {
                                setDeliveryAddress(place.formatted_address);
                                calculateDistance(place.geometry.location);
                              }
                            }}
                          options={autocompleteOptions}
                        >
                          <input
                            id="deliveryAddress"
                            type="text"
                            placeholder="Anna toimitusosoite..."
                            value={deliveryAddress}
                            onChange={(e) => {
                              setDeliveryAddress(e.target.value);
                                if (e.target.value.trim().length < 3) {
                                  setDeliveryDistance(null);
                                }
                            }}
                            disabled={deliveryOption !== "delivery"}
                            aria-invalid={deliveryRequired && !isValidAddress}
                          className={
                              deliveryOption !== "delivery"
                                ? `${disabledInputClass} text-center`
                                : !isValidAddress && deliveryRequired
                                  ? `${inputClass} border-red-500 text-center focus:border-red-500 focus:ring-red-500/30`
                                  : `${inputClass} text-center`
                            }
                          />
                        </Autocomplete>

                        {deliveryRequired && !isValidAddress && (
                        <p className={errorTextClass}>
                          Anna kelvollinen osoite
                        </p>
                        )}
                      </div>
                    </div>
                  )}

                  {autoMessageOption === "reservation" && (
                    <div className={innerSectionClass}>
                      <h3 className={sectionTitleClass}>
                        Maksutapa
                      </h3>

                      <div className="mt-4 space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="payment_method"
                            value="invoice"
                            checked={paymentMethod === "invoice"}
                            onChange={() => setPaymentMethod("invoice")}
                          />
                          <span className={radioTextClass}>Lasku</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="payment_method"
                            value="mobilepay"
                            checked={paymentMethod === "mobilepay"}
                            onChange={() => setPaymentMethod("mobilepay")}
                          />
                          <span className={radioTextClass}>MobilePay</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="payment_method"
                            value="cash"
                            checked={paymentMethod === "cash"}
                            onChange={() => setPaymentMethod("cash")}
                          />
                          <span className={radioTextClass}>Käteinen</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Kaksi saraketta: vasen => yhteystiedot, oikea => viesti + submit */}
                  <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                    {/* Sarake 1: nimi, sposti, puh, tapahtumapaikka (jos ei trailer), kuljetus (jos ei trailer) */}
                    <div className="space-y-5">
                      <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                      >
                        <label
                          htmlFor="name"
                          className={labelClass}
                        >
                          Nimesi:
                        </label>
                        <motion.input
                          id="name"
                          type="text"
                          placeholder="Etu- ja sukunimi.."
                          value={nimi}
                          onChange={(e) => setNimi(e.target.value)}
                          required
                          whileFocus={{
                            scale: 1.05,
                            transition: {
                              duration: 0.2,
                              type: "spring",
                              stiffness: 300,
                            },
                          }}
                          className={inputClass}
                        />
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      >
                        <label htmlFor="email" className={labelClass}>
                          Sähköposti
                        </label>
                        <motion.input
                          id="email"
                          type="email"
                          placeholder="Syötä sähköpostisi.."
                          value={sahkoposti}
                          onChange={(e) => setSahkoposti(e.target.value)}
                          onBlur={(e) => validateEmail(e.target.value)} // tarkistus blurissa
                          required
                          pattern="[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}"
                          title="Anna voimassa oleva sähköpostiosoite"
                          whileFocus={{
                            scale: 1.05,
                            transition: {
                              duration: 0.2,
                              type: "spring",
                              stiffness: 300,
                            },
                          }}
                          className={inputClass}
                        />

                        {/* Virheilmoitus näkyy vain jos virhe on olemassa */}
                        {emailError && (
                          <p className={errorTextClass}>{emailError}</p>
                        )}
                      </motion.div>

                      <motion.div
                          initial={{ opacity: 0, x: -50 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: 0.3 }}
                        >
                        <label htmlFor="puhelinnumero" className={labelClass}>
                          Puhelinnumero
                        </label>
                          <motion.input
                          id="puhelinnumero"
                          type="tel"
                          placeholder="Anna puhelinnumero.."
                          value={puhelinnumero}
                          onChange={(e) => setPuhelinnumero(e.target.value)}
                          onBlur={(e) => validatePhone(e.target.value)} // validointi blurilla
                          required
                          pattern="[0-9\s\-\+\(\)]{7,20}" // korjattu pattern!
                          title="Anna voimassa oleva puhelinnumero"
                          whileFocus={{
                            scale: 1.05,
                            transition: {
                              duration: 0.2,
                              type: "spring",
                              stiffness: 300,
                            },
                          }}
                          className={inputClass}
                        />

                          {phoneError && (
                            <p className={errorTextClass}>{phoneError}</p>
                          )}
                        </motion.div>

                      {autoMessageOption === "reservation" && !transportNeedsTrailer && (
                        <motion.div
                          initial={{ opacity: 1 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                     
                          className="min-h-[70px]"
                        >
                        <label
                          htmlFor="location"
                          className={
                            deliveryOption === "delivery"
                              ? "mb-2 block font-customFont1 text-sm font-extrabold uppercase tracking-[0.1em] text-gray-500 opacity-50"
                              : labelClass
                          }
                        >
                          Tapahtumapaikka
                        </label>

                          <Autocomplete
                            onLoad={(ref) => (locationRef.current = ref)}
                            onPlaceChanged={() => {
                              console.log("LOCATION onPlaceChanged");
                              const place = locationRef.current?.getPlace();
                              console.log("LOCATION place:", place);

                              if (place?.formatted_address) {
                                setTapahtumapaikka(place.formatted_address);
                              }
                            }}
                            options={autocompleteOptions}
                          >

                          <input
                            id="location"
                            type="text"
                            placeholder="Tapahtumapaikan osoite.."
                            value={tapahtumapaikka}
                            onChange={(e) => setTapahtumapaikka(e.target.value)}
                            disabled={deliveryOption === "delivery"}
                            className={
                              deliveryOption === "delivery"
                                ? disabledInputClass
                                : inputClass
                            }
                          />
                        </Autocomplete>

                        </motion.div>
                      )}

                    </div>

                    <div className="space-y-5">
                      <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.6 }}
                      >
                        <label
                          htmlFor="viesti"
                          className={labelClass}
                        >
                          Viestisi
                          {transportNeedsTrailer && autoMessageOption === "reservation" && (
                            <span className="text-sm font-semibold normal-case tracking-normal !text-[#4B5563] dark:!text-[#D1D5DB]">
                              {" "}
                              (valinnainen)
                            </span>
                          )}
                        </label>
                      <motion.textarea
                        id="viesti"
                        placeholder={
                          transportNeedsTrailer && autoMessageOption === "reservation"
                            ? "Jos haluat, voit lisätä lisätietoja..."
                            : "Kirjoita viestisi..."
                        }
                        value={viesti}
                        onChange={(e) => setViesti(e.target.value)}
                        rows="4"
                        required={!(transportNeedsTrailer && autoMessageOption === "reservation")}
                        whileFocus={{
                          scale: 1.05,
                          transition: {
                            duration: 0.2,
                            type: "spring",
                            stiffness: 300,
                          },
                        }}
                        className={`${inputClass} min-h-[160px] resize-y rounded-3xl`}
                      />

                      </motion.div>

                      <div className="my-4">
                      <RecaptchaField
                        siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(val) => setRecaptchaRentalValue(val)}
                        innerRef={recaptchaRef}
                      />
                    </div>
                      <ResponsiveButton type="submit" className="w-full">
                        Lähetä
                      </ResponsiveButton>
                    </div>
                  </div>
                </form>
              </div>

              <motion.div
                className={`${sectionBoxClass} relative mx-auto mt-4 w-full max-w-lg lg:mt-0`}
                initial={{ opacity: 1 }}
                animate={{ opacity: autoMessageOption === "message" ? 0.3 : 1 }}
                transition={{ duration: 0.3 }}
                layout
              >
                <span className={badgeClass}>Yhteenveto</span>

                <h3 className="mt-3 font-customFont2 text-4xl font-bold uppercase leading-none !text-[#111827] dark:!text-white">
                  🛒 Vuokrauskori
                </h3>

                <ul className="space-y-4">
                  {/* 📦 Vuokrattavat tuotteet */}
                  {cart.map((item, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center border-b border-white/30 pb-3"
                    >
                      <div className="flex items-center">
                        <p className="font-customFont1 text-base font-semibold !text-[#111827] dark:!text-white">
                          {item.name}
                        </p>
                        {item.quantity > 1 && (
                        <span className="ml-2 font-customFont1 text-base font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                          ({item.quantity}x)
                        </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        <p className="font-customFont1 text-base font-extrabold !text-[#111827] dark:!text-white whitespace-nowrap">
                          {item.flags?.isTrailer
                            ? Number(item.pricing?.totalPrice || 0).toFixed(2)
                            : calculateRentalPrice(
                                selectedDates.startDate,
                                selectedDates.endDate,
                                item.price,
                                item.quantity
                              ).toFixed(2)
                          } €
                        </p>

                        <button
                          onClick={() => handleRemoveFromCart(index)}
                          className="bg-teal-500 text-white font-bold w-7 h-7 flex items-center justify-center rounded-full shadow-md transition-all hover:bg-red-600 hover:scale-110"
                        >
                          <span className="-translate-y-[0.08rem]">&times;</span>
                        </button>
                      </div>
                    </li>
                  ))}

                  {deliveryRequired && deliveryDistance != null && (
                    <li className="flex justify-between items-center border-b border-white/30 pb-3">
                      <span className="font-customFont1 text-base font-semibold !text-[#111827] dark:!text-white">
                        Vuokra yhteensä
                      </span>
                      <span className="font-customFont1 text-base font-semibold !text-[#111827] dark:!text-white">
                        {frontendRentalTotal.toFixed(2)} €
                      </span>
                    </li>
                  )}


                {deliveryRequired && deliveryDistance != null && (
                  <li className="border-b border-white/30 pb-3 space-y-2">
                    {/* Otsikko */}
                
                    {/* Kilometrit */}
                    <div className="font-customFont1 text-sm font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                      Kilometrikorvaus: 0,59 € / km × {(deliveryDistance * 2).toFixed(1)} km ={" "}
                      {round2(deliveryDistance * 2 * 0.59).toFixed(2)} €
                    </div>

                    {/* Peräkärrymaksu */}
                    {transportNeedsTrailer && (
                      <div className="font-customFont1 text-sm font-semibold !text-[#4B5563] dark:!text-[#D1D5DB]">
                        Peräkärrymaksu: 2 × 20,00 € = 40,00 €
                      </div>
                    )}

                      <div className="flex justify-between">
                      <span className="font-customFont1 text-base font-semibold !text-[#111827] dark:!text-white">
                        Kuljetus yhteensä (edestakainen)
                      </span>
                      <span className="font-customFont1 text-base font-extrabold !text-[#111827] dark:!text-white whitespace-nowrap">
                        {frontendTransportCost.toFixed(2)} €
                      </span>
                    </div>
                  </li>
                )}

                </ul>

                {/* 💰 Kokonaissumma */}
                <div className="mt-6 flex items-center justify-between border-t border-[#DDE7E5] pt-5 font-customFont1 text-xl font-extrabold !text-[#111827] dark:border-darkborder dark:!text-white">
                  <p>Yhteensä:</p>
                  <p>
                    {reservationResult
                      ? reservationResult.total_price.toFixed(2)
                      : (frontendRentalTotal + frontendTransportCost).toFixed(2)
                    } €
                  </p>
                </div>

              </motion.div>
            </div>
          </motion.div>
        </div>
      </main>
    </LoadScript>
    </>
  );
};

export default Contactpage;
